import { useState, useEffect, useRef, useCallback } from "react";
import { trpc } from "../trpc";
import type { EditorState, EditorAction, EditorDraft } from "./editor-types";
import { normalizeServerToDraft, normalizeDraftForSave } from "./draft-normalizer";
import { performThreeWayRebase, type ThreeWayRebaseResult } from "./three-way-rebase";

export type AutosaveStatus = "idle" | "saving" | "saved" | "error" | "conflict" | "offline";

interface UseAutosaveOptions {
  enabled?: boolean;
  onIdMappings?: (mappings: Record<string, string>) => void;
  onProFeatureRequired?: (violations: string[]) => void;
}

export function useAutosave(
  editorState: EditorState,
  dispatch: React.Dispatch<EditorAction>,
  options: UseAutosaveOptions = { enabled: true }
) {
  const { enabled = true, onIdMappings, onProFeatureRequired } = options;
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflictResult, setConflictResult] = useState<ThreeWayRebaseResult | null>(null);

  const saveMutation = trpc.profile.saveBulkDraft.useMutation();
  const utils = trpc.useUtils();

  const saveMutationRef = useRef(saveMutation);
  const utilsRef = useRef(utils);
  const dispatchRef = useRef(dispatch);
  const onIdMappingsRef = useRef(onIdMappings);
  const onProFeatureRequiredRef = useRef(onProFeatureRequired);

  const stateRef = useRef<EditorState>(editorState);
  const statusRef = useRef<AutosaveStatus>(status);
  const lastErrorRef = useRef<any>(null);
  const baseDraftRef = useRef<EditorDraft | null>(editorState.draft);
  const acknowledgedRevisionRef = useRef<number | null>(editorState.acknowledgedRevision);

  // Helper to synchronously update both ref and React state
  const setStatusSync = useCallback((newStatus: AutosaveStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
  }, []);

  // Flight mutex and promises
  const isSavingRef = useRef<boolean>(false);
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep refs up to date
  useEffect(() => {
    saveMutationRef.current = saveMutation;
    utilsRef.current = utils;
    dispatchRef.current = dispatch;
    onIdMappingsRef.current = onIdMappings;
    onProFeatureRequiredRef.current = onProFeatureRequired;
  });

  useEffect(() => {
    stateRef.current = editorState;
    if (editorState.acknowledgedRevision !== null) {
      acknowledgedRevisionRef.current = editorState.acknowledgedRevision;
    }
    if (!baseDraftRef.current && editorState.draft) {
      baseDraftRef.current = editorState.draft;
    }
  }, [editorState]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  /**
   * Serialized Save Loop:
   * Executes single flight at a time. If new edits occur while in flight,
   * continues looping until all edits are persisted or an unresolved conflict occurs.
   */
  const executeSaveLoop = useCallback(async (): Promise<void> => {
    if (isSavingRef.current && inFlightPromiseRef.current) {
      return inFlightPromiseRef.current;
    }

    isSavingRef.current = true;
    setStatusSync("saving");

    const flightPromise = (async () => {
      try {
        while (
          stateRef.current.draft &&
          stateRef.current.acknowledgedRevision !== null &&
          stateRef.current.editSequence > stateRef.current.savedSequence
        ) {
          const snapshot = stateRef.current.draft;
          const sequenceToSave = stateRef.current.editSequence;
          const expectedVersion = acknowledgedRevisionRef.current ?? stateRef.current.acknowledgedRevision;

          try {
            // Pure draft normalization produces a safe network snapshot without mutating local React state
            const networkDraft = normalizeDraftForSave(snapshot);

            const profilePayload = {
              ...networkDraft.profile,
              fullName: networkDraft.profile.fullName || "Scholar",
              title: networkDraft.profile.title || null,
              bio: networkDraft.profile.bio || null,
              photoUrl: networkDraft.profile.photoUrl || null,
              colorPalette: (networkDraft.profile.colorPalette || undefined) as any,
              themeKey: (networkDraft.profile.themeKey || undefined) as any,
              fontPairing: (networkDraft.profile.fontPairing || undefined) as any,
              backgroundStyle: (networkDraft.profile.backgroundStyle || undefined) as any,
              headerStyle: (networkDraft.profile.headerStyle || undefined) as any,
              animationLevel: (networkDraft.profile.animationLevel || undefined) as any,
              layoutDensity: (networkDraft.profile.layoutDensity || undefined) as any,
              cornerRadius: (networkDraft.profile.cornerRadius || undefined) as any,
              sectionArrangement: (networkDraft.profile.sectionArrangement || undefined) as any,
              typographyScale: (networkDraft.profile.typographyScale || undefined) as any,
              language: networkDraft.profile.language || "en",
              layoutOverrides: (networkDraft.profile.layoutOverrides as Record<string, unknown>) || {},
              customColors: networkDraft.profile.customColors || null,
              seoOverrides: (networkDraft.profile.seoOverrides as Record<string, unknown>) || {},
            };

            const cleanedSections = (networkDraft.sections || []).map((s) => ({
              id: s.id,
              type: s.type,
              pageId: (s as any).pageId || undefined,
              visible: Boolean(s.visible),
              displayOrder: Number(s.displayOrder ?? 0),
              content: (s.content as Record<string, unknown>) || {},
            }));

            const result = await saveMutationRef.current.mutateAsync({
              draft: {
                profile: profilePayload,
                sections: cleanedSections,
              } as any,
              expectedDraftVersion: expectedVersion,
            });

            acknowledgedRevisionRef.current = result.newRevision;
            baseDraftRef.current = snapshot;

            // Immediately update React Query profile & sections cache to avoid status flashing
            utilsRef.current.profile.getMyProfile.setData(undefined, (old: any) =>
              old ? { ...old, draftVersion: result.newRevision } : old
            );

            utilsRef.current.profile.getSections.setData(undefined, () =>
              cleanedSections.map((sec, idx) => ({
                id: result.idMappings?.[sec.id] || sec.id,
                profileId: profilePayload.id || "",
                pageId: sec.pageId,
                type: sec.type,
                visible: sec.visible,
                displayOrder: sec.displayOrder ?? idx,
                content: sec.content,
                createdAt: new Date(),
                updatedAt: new Date(),
              })) as any
            );

            // Invalidate pages list to ensure page section counters stay in sync
            utilsRef.current.pages.listPages.invalidate();

            stateRef.current = {
              ...stateRef.current,
              savedSequence: sequenceToSave,
              acknowledgedRevision: result.newRevision,
            };

            dispatchRef.current({
              type: "SAVE_SUCCEEDED",
              payload: {
                newRevision: result.newRevision,
                idMappings: result.idMappings,
                savedSequence: sequenceToSave,
              },
            });

            if (result.idMappings && Object.keys(result.idMappings).length > 0) {
              onIdMappingsRef.current?.(result.idMappings);
            }

            setStatusSync("saved");
            lastErrorRef.current = null;
            setConflictResult(null);
          } catch (err: unknown) {
            lastErrorRef.current = err;

            const isConflict =
              (err as { data?: { code?: string } })?.data?.code === "CONFLICT" ||
              (err as { shape?: { data?: { code?: string } } })?.shape?.data?.code === "CONFLICT" ||
              (err as { status?: number })?.status === 409 ||
              (err as Error)?.message?.includes("CONFLICT") ||
              (err as Error)?.message?.includes("newer version");

            if (isConflict) {
              setStatusSync("conflict");
              // Fetch fresh server state
              const [freshProfile, freshSections] = await Promise.all([
                utilsRef.current.profile.getMyProfile.fetch(),
                utilsRef.current.profile.getSections.fetch(),
              ]);

              if (freshProfile && freshSections && baseDraftRef.current && stateRef.current.draft) {
                const freshServerDraft = normalizeServerToDraft(freshProfile, freshSections);
                const rebase = performThreeWayRebase(
                  baseDraftRef.current,
                  stateRef.current.draft,
                  freshServerDraft
                );

                if (!rebase.hasCollisions) {
                  // Safe automatic rebase!
                  dispatchRef.current({
                    type: "SET_INITIAL_DRAFT",
                    payload: rebase.rebasedDraft,
                  });
                  baseDraftRef.current = freshServerDraft;
                  acknowledgedRevisionRef.current = freshServerDraft.draftVersion;
                  setStatusSync("idle");
                  lastErrorRef.current = null;
                  // Continue loop to persist rebased draft
                  continue;
                } else {
                  // True collision detected -> require user resolution
                  setConflictResult(rebase);
                  break;
                }
              }
              break;
            } else {
              setStatusSync("error");

              const isProFeatureError =
                (err as Error)?.message === "PRO_FEATURE_REQUIRED" ||
                (err as any)?.shape?.message === "PRO_FEATURE_REQUIRED" ||
                (err as any)?.data?.message === "PRO_FEATURE_REQUIRED";

              if (isProFeatureError) {
                const violations: string[] =
                  (err as any)?.cause?.violations ||
                  (err as any)?.data?.cause?.violations ||
                  (err as any)?.shape?.data?.cause?.violations ||
                  [];
                onProFeatureRequiredRef.current?.(violations);
                setErrorMessage("Pro feature required: Upgrade your plan to save these features.");
              } else {
                let msg = "Unable to save draft";
                try {
                  const rawMsg = (err as any)?.shape?.message || (err as any)?.message || "";
                  const parsed = JSON.parse(rawMsg);
                  if (Array.isArray(parsed) && parsed[0]?.path) {
                    msg = `Unable to save: ${parsed[0].path.join(" → ")} (${parsed[0].message})`;
                  }
                } catch {
                  if ((err as Error)?.message) {
                    msg = `Unable to save: ${(err as Error).message}`;
                  }
                }
                setErrorMessage(msg);
              }
              break;
            }
          }
        }
      } finally {
        isSavingRef.current = false;
        inFlightPromiseRef.current = null;
        if (statusRef.current === "saved") {
          setTimeout(() => {
            if (stateRef.current.editSequence === stateRef.current.savedSequence && statusRef.current === "saved") {
              setStatusSync("idle");
            }
          }, 2000);
        }
      }
    })();

    inFlightPromiseRef.current = flightPromise;
    return flightPromise;
  }, []);

  /**
   * Conflict Resolution Handlers
   */
  const resolveConflictKeepLocal = useCallback(async () => {
    if (!conflictResult || !stateRef.current.draft) return;
    try {
      const fresh = await utilsRef.current.profile.getMyProfile.fetch();
      if (fresh) {
        acknowledgedRevisionRef.current = fresh.draftVersion;
        baseDraftRef.current = conflictResult.rebasedDraft;
        setConflictResult(null);
        setStatusSync("idle");
        lastErrorRef.current = null;
        await executeSaveLoop();
      }
    } catch (err: unknown) {
      lastErrorRef.current = err;
      setStatusSync("error");
    }
  }, [conflictResult, executeSaveLoop, setStatusSync]);

  const resolveConflictUseServer = useCallback(async () => {
    try {
      const [freshProfile, freshSections] = await Promise.all([
        utilsRef.current.profile.getMyProfile.fetch(),
        utilsRef.current.profile.getSections.fetch(),
      ]);
      if (freshProfile && freshSections) {
        const freshDraft = normalizeServerToDraft(freshProfile, freshSections);
        dispatchRef.current({
          type: "SET_INITIAL_DRAFT",
          payload: freshDraft,
        });
        baseDraftRef.current = freshDraft;
        acknowledgedRevisionRef.current = freshDraft.draftVersion;
        setConflictResult(null);
        setStatusSync("idle");
        lastErrorRef.current = null;
      }
    } catch (err: unknown) {
      lastErrorRef.current = err;
      setStatusSync("error");
    }
  }, [setStatusSync]);

  /**
   * Explicit Flush for Navigation & Publish Safety
   */
  const flush = useCallback(async (): Promise<void> => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (inFlightPromiseRef.current) {
      await inFlightPromiseRef.current;
    }

    if (stateRef.current.editSequence > stateRef.current.savedSequence) {
      await executeSaveLoop();
    }

    if (statusRef.current === "conflict") {
      throw new Error("Cannot proceed due to an unresolved version conflict. Please resolve differences first.");
    }
    if (statusRef.current === "error") {
      throw lastErrorRef.current || new Error("Failed to save changes to the server. Please check your connection.");
    }
  }, [executeSaveLoop]);

  // Debounced Autosave Trigger
  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    if (!editorState.draft || editorState.editSequence === editorState.savedSequence) {
      return;
    }

    if (status === "conflict") return; // Halt automated debounces during unresolved conflict

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      executeSaveLoop();
    }, 2000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, editorState.editSequence, editorState.savedSequence, editorState.draft, executeSaveLoop, status]);

  /**
   * Explicitly persists a specific snapshot through the serialized save loop mutex.
   * Ensures any transient design changes or latest user edits are immediately queued,
   * persisted, acknowledged, and returned with confirmed newRevision.
   */
  const saveSnapshot = useCallback(async (snapshot: EditorDraft): Promise<number> => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (inFlightPromiseRef.current) {
      await inFlightPromiseRef.current;
    }

    stateRef.current = {
      ...stateRef.current,
      draft: snapshot,
      editSequence: stateRef.current.editSequence + 1,
    };

    await executeSaveLoop();

    if (statusRef.current === "conflict") {
      throw new Error("Cannot proceed due to an unresolved version conflict. Please resolve differences first.");
    }
    if (statusRef.current === "error") {
      throw lastErrorRef.current || new Error("Failed to save draft to the server. Please check your connection.");
    }

    const confirmedVersion =
      acknowledgedRevisionRef.current ??
      stateRef.current.acknowledgedRevision ??
      snapshot.draftVersion;
    return confirmedVersion;
  }, [executeSaveLoop]);

  return {
    status,
    errorMessage,
    flush,
    saveSnapshot,
    conflictResult,
    resolveConflictKeepLocal,
    resolveConflictUseServer,
  };
}
